/**
 * Platform selection component for social media platforms
 */

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { platforms } from "./constants";

export function PlatformSelector({ 
  connectedPlatforms, 
  selectedPlatforms, 
  onPlatformToggle 
}) {
  const navigate = useNavigate();

  if (connectedPlatforms.length === 0) {
    return (
      <div className="card-base p-6">
        <label className="text-sm text-viraly-muted mb-3 block">Select Platforms</label>
        <div 
          className="text-center py-6 border border-dashed border-white/20 rounded-xl"
          data-testid="no-platforms-message"
        >
          <p className="text-viraly-muted mb-2">No social platforms connected yet</p>
          <p className="text-xs text-viraly-muted mb-4">
            Connect your accounts to post content. It's <span className="text-viraly-orange font-semibold">FREE</span> to post to multiple platforms!
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/settings?tab=social")}
            className="border-viraly-orange text-viraly-orange hover:bg-viraly-orange/10"
            data-testid="connect-platforms-btn"
          >
            Connect Platforms
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-base p-6">
      <label className="text-sm text-viraly-muted mb-3 block">Select Platforms</label>
      <div className="flex gap-3">
        {platforms.map((p) => {
          const isConnected = connectedPlatforms.includes(p.id);
          const isSelected = selectedPlatforms.includes(p.id);
          
          return (
            <button
              key={p.id}
              onClick={() => {
                if (!isConnected) return;
                onPlatformToggle(p.id);
              }}
              disabled={!isConnected}
              data-testid={`platform-${p.id}`}
              className={`flex-1 p-4 rounded-xl border-2 transition-all relative ${
                !isConnected 
                  ? "border-white/5 opacity-40 cursor-not-allowed"
                  : isSelected
                    ? "border-viraly-orange bg-viraly-orange/10"
                    : "border-white/10 hover:border-white/20"
              }`}
            >
              {!isConnected && (
                <div className="absolute -top-2 -right-2 bg-white/10 text-[10px] px-2 py-0.5 rounded-full">
                  Not connected
                </div>
              )}
              <div 
                className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center"
                style={{ backgroundColor: isConnected ? p.color : "#444" }}
              >
                {p.name[0]}
              </div>
              <p className="text-sm font-medium">{p.name}</p>
            </button>
          );
        })}
      </div>
      
      {/* Prompt to connect more platforms */}
      {connectedPlatforms.length < 3 && (
        <div className="mt-4 p-3 bg-viraly-orange/10 border border-viraly-orange/20 rounded-lg">
          <p className="text-sm text-viraly-orange">
            💡 Connect more platforms to reach a wider audience - it's <span className="font-semibold">FREE</span>! 
            Same content, multiple channels, no extra cost.
          </p>
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate("/settings?tab=social")}
            className="text-viraly-orange p-0 h-auto mt-1"
          >
            Connect {3 - connectedPlatforms.length} more platform{connectedPlatforms.length < 2 ? 's' : ''} →
          </Button>
        </div>
      )}
    </div>
  );
}
